/* istanbul ignore file */
/* eslint-disable import/no-duplicates */
import * as fns from 'date-fns'
import { differenceInMilliseconds, millisecondsInMinute } from 'date-fns'
import * as fnsLocales from 'date-fns/locale'
/* eslint-enable import/no-duplicates */
import * as fnsTz from 'date-fns-tz'

import i18n from '@lib/i18n'

const localesMap: Record<Locale, any> = {
  ar: fnsLocales.ar,
  bg: fnsLocales.bg,
  cs: fnsLocales.cs,
  da: fnsLocales.da,
  de: fnsLocales.de,
  en: fnsLocales.enGB,
  es: fnsLocales.es,
  fr: fnsLocales.fr,
  hr: fnsLocales.hr,
  it: fnsLocales.it,
  ja: fnsLocales.ja,
  nl: fnsLocales.nl,
  no: fnsLocales.nn,
  pl: fnsLocales.pl,
  pt: fnsLocales.pt,
  sq: fnsLocales.sq,
  sv: fnsLocales.sv,
  tr: fnsLocales.tr,
  vi: fnsLocales.vi,
  zh: fnsLocales.zhCN,
  'en-IE': fnsLocales.enIE,
  'en-US': fnsLocales.enUS,
  'en-CA': fnsLocales.enCA,
  'es-CO': fnsLocales.es,
  'es-EC': fnsLocales.es,
  'es-MX': fnsLocales.es,
  'hi-IN': fnsLocales.hi,
  'pt-BR': fnsLocales.ptBR,
  'sk-SK': fnsLocales.sk,
  'th-TH': fnsLocales.th,
  ur: fnsLocales.enGB,
  bs: fnsLocales.bs,
  el: fnsLocales.el,
  hu: fnsLocales.hu,
  mk: fnsLocales.mk,
  ro: fnsLocales.ro,
  ru: fnsLocales.ru,
  sr: fnsLocales.sr,
  uk: fnsLocales.uk,
  yue: fnsLocales.enGB,
  id: fnsLocales.id,
  lv: fnsLocales.lv,
  ga: fnsLocales.enGB,
  rm: fnsLocales.enGB,
  he: fnsLocales.he,
  sw: fnsLocales.enGB,
}

interface DurationFormaOptions {
  format?: string[]
  zero?: boolean
  delimiter?: string
}

interface DurationFormat {
  years?: number
  months?: number
  weeks?: number
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
}

export const DATE_FORMATS = {
  WEEKDAY_AND_DATE: 'EEE, dd MMM yyyy',
  DATE_WITH_MONTH_NAME: 'MMM dd, yyyy',
}

type Duration = 'months' | 'hours' | 'minutes'

const trimCountryCode = (locale: Locale): Locale => locale.split('-')[0].toLowerCase() as Locale

export const getLocale = (localeCode?: Locale): any => {
  const locale = localeCode ?? i18n.getLanguage()
  const trimmedLocale = trimCountryCode(locale)
  return localesMap[locale] ?? localesMap[trimmedLocale] ?? fnsLocales.enGB
}

const parse = (string: string, tz?: TimeZone): Date => {
  if (tz !== 'UTC') return fns.parseISO(string)

  // The rAPI has started passing dates with the time zone, but only for newly created fields,
  // so we need to remove the time zone to ensure the similar format with the older ones.
  const parsedDate = fns.parseISO(`${string.split('+')[0]}+00:00`)

  return fnsTz.utcToZonedTime(parsedDate, tz)
}

// TODO: delete this function and use roundToNearestHours after updating date-fns library
export function roundDateToNextHourFNS(date: Date) {
  if (!(date instanceof Date) || !fns.isValid(date)) {
    console.warn('Invalid date passed to roundToNextHour:', date)
    return date
  }

  if (date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0) {
    return date
  }

  const nextHour = fns.addHours(date, 1)
  return fns.setMilliseconds(fns.setSeconds(fns.setMinutes(nextHour, 0), 0), 0)
}

const today = (): Date => new Date()

const formatInLocal = (date: Date, template: string): string => fns.format(date, template, { locale: getLocale() })
const formatInUtc = (date: Date, template: string): string =>
  fnsTz.formatInTimeZone(date, 'UTC', template, {
    locale: getLocale(),
  })

const format = (date: Date, template: string, displayInUtc = false): string =>
  displayInUtc ? formatInUtc(date, template) : formatInLocal(date, template)

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd')

const formatTime = (date: Date, displayInUtc = false): string => format(date, 'HH:mm', displayInUtc)

const formatDateISO = (date: Date): string => format(date, "yyyy-MM-dd'T'HH:mm")

const parseUTCDateTime = <T extends string | null>(
  date: T,
  time: string | null,
  tz?: TimeZone,
): T extends null ? Date | null : Date => (date == null ? (null as any) : parse(`${date}T${time ?? ''}+00:00`, tz))

const formatYearMonth = (date: Date): string => format(date, 'MMMM yyyy')

const formatDateTimeText = (date: Date): string => format(date, 'MMM dd, yyyy, HH:mm')

const formatDateText = (date: Date): string => format(date, 'MMM dd, yyyy')

const formatDateNumeric = (date: Date): string => {
  const locale = i18n.getLanguage()

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const durationToObject = (start: Date | number, end: Date | number, template: Duration[]): Record<string, number> => {
  const formatted: globalThis.Duration = fns.intervalToDuration({ start, end })
  const format = {
    months: (formatted.years as number) * 12 + (formatted.months as number),
    hours: (formatted.days as number) * 24 + (formatted.hours as number),
    minutes: formatted.minutes,
  }

  return Object.fromEntries(
    Object.entries(format).filter(([key, value]) => template?.includes(key as Duration) && !!(value as number)),
  ) as Record<string, number>
}

export const durationToString = (start: Date | number, end: Date | number, template: Duration[]): string =>
  Object.entries(durationToObject(start, end, template))
    .map(([key, value]) => {
      const replaced = key.replace('hours', 'h').replace('minutes', 'min')

      return `${value}${replaced}`
    })
    .join(' ')

const ceilHour = (date: Date): string => {
  const updatedDate = fns.setMinutes(fns.addHours(date, 1), 0)

  return format(updatedDate, 'HH:mm')
}

const startOfDay = (): string => '00:00'

const endOfDay = (): string => '23:59'

const formatDuration = (duration: DurationFormat, options?: DurationFormaOptions): string => {
  return fns.formatDuration(duration, { ...options, locale: getLocale() })
}

const isAfterOrEqual = (a: Date, b: Date): boolean => fns.isAfter(a, b) || fns.isEqual(a, b)

const isValidDateString = (string: string, regex?: RegExp): boolean => {
  const dateRegex = regex ?? /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

  return dateRegex.test(string)
}

const getFrequency = (sortedDates: string[]): number => {
  const difference = sortedDates
    .map((date, index, self) => (self[index + 1] ? differenceInMilliseconds(parse(self[index + 1]), parse(date)) : 0))
    .reduce((acc, curr) => acc + curr, 0)

  return Math.ceil(difference / sortedDates.length / millisecondsInMinute)
}

export const formatTimeHHmm = (date: string, displayInUtc = false): string => {
  return format(new Date(date), 'HH:mm', displayInUtc)
}

const addDuration = (
  date: Date,
  amount: number,
  type: 'months' | 'days' | 'hours' | 'minutes',
  includeStartDate?: boolean,
): Date => {
  switch (type) {
    case 'months':
      return fns.subDays(fns.addMonths(date, amount), includeStartDate ? 1 : 0)
    case 'days':
      return fns.addDays(date, includeStartDate ? amount - 1 : amount)
    case 'hours':
      return fns.addHours(date, amount)
    case 'minutes':
      return fns.addMinutes(date, amount)
    default:
      throw new Error(`Unsupported duration type: ${type}`)
  }
}

const addMonths = (date: Date, months: number): Date => {
  return fns.addMonths(date, months)
}

const endOfMonth = (date: Date): Date => {
  return fns.endOfMonth(date)
}

export default {
  today,
  parse,
  format,
  formatYearMonth,
  durationToObject,
  formatDate,
  formatTime,
  formatDateISO,
  durationToString,
  ceilHour,
  startOfDay,
  endOfDay,
  parseUTCDateTime,
  formatDuration,
  formatDateTimeText,
  formatDateText,
  formatDateNumeric,
  isAfterOrEqual,
  isValidDateString,
  getFrequency,
  addDuration,
  addMonths,
  endOfMonth,
}
